// src/api/checks.js
//
// Thin client for the proxy's Conformance Engine REST endpoints
// (proxy-server/apiServer.js — "Conformance Engine" section, backed by
// proxy-server/conformance/{runSuite,drivenRun,compileCheck}.js).
//
// These live on the PROXY REST API (not the backend) — same base other
// `/proxy/*` calls use (IuRewritePage.jsx, api/appPacks.js):
// REACT_APP_PROXY_API_URL in prod (https://proxy-api.adlensfx.com), falls
// back to a bare "/proxy" path relative to the page origin for local dev
// (no separate proxy origin configured).
//
//   GET  /proxy/checks/suites                 -> { suites: [{ name, count, source:'builtin'|'user', updatedAt? }] }
//   POST /proxy/checks/suites    { name, checks[] } -> { ok, name, count }
//                                                  400 { error:'validation_failed', issues[] }
//                                                  (name rule [a-z0-9-]{1,64}; builtin names rejected)
//   POST /proxy/checks/suites/<name>/append { check } -> { ok, name, count, replaced }
//                                                  (creates the suite if absent; a same-id check is replaced)
//   DELETE /proxy/checks/suites/<name>        -> { ok, name } | 403 (builtin) | 404 (unknown)
//   GET  /proxy/checks/suites/<name>          -> { name, checks: [...Check] } (WP12 — full definitions)
//   GET  /proxy/checks/runs?limit=            -> { runs: [{ id, ts, app, suite, inline, adOnly,
//                                                  appAdsDomain, rowCount, rollup:{pass,warn,fail,na,info},
//                                                  checks:[{id,status,matched,found}], trigger:'run'|'audit'|'auto' }] }
//                                                  newest-first
//   GET  /proxy/checks/runs/<id>              -> a single run record (same shape as a runs[] entry)
//   GET  /proxy/checks/trends?checkId=&limit= -> { checkId, trend:[{ runId, ts, status }] } newest-first
//   POST /proxy/checks/compile   { text, app? } -> { ok, check, issues, rationale, raw }
//                                                  409 { error: 'anthropic_api_key_missing', detail }
//   POST /proxy/checks/run       { suite?, checks?, rows?, app?, signals? }
//                                              -> { suite, results, rollup, session }
//   POST /proxy/checks/run-driven { suite?, checks?, app, platform?, windowSec?, device?, signals? }
//                                              -> { suite, app, platform, plan, results, rollup, session, driven }
//   POST /proxy/checks/discover  { rows?, app? } -> { ok, proposal:{authSignal, contentApis, labels},
//                                              rationale, confidence, issues, raw, profile }
//                                                  409 { error: 'anthropic_api_key_missing', detail }
//   POST /proxy/checks/propose-suite { rows?, app?, max? } -> { ok, checks:[...Check], dropped,
//                                              rationale, coverage:[...], issues, raw }
//                                                  409 { error: 'anthropic_api_key_missing', detail }
//   POST /proxy/checks/audit    { rows?, app?, max? } -> { ok, signals, proposal, run,
//                                              usedDiscoveredSignals, checkSource, note? }
//                                                  409 { error: 'anthropic_api_key_missing', detail }
//   POST /proxy/checks/session   { rows, meta?, append?, sessionId? }
//                                              -> { sessionId, rowCount, bodiesRetained? }
//                                              Interactive Analyst WP0 (docs/interactive-analyst-spec.md
//                                              §3.2): a server-side "analysis session" holding rows
//                                              WITH bodies for the life of a conversation, so a later
//                                              /proxy/checks/ask?sessionId=... can read raw evidence the
//                                              two-tier live history already stripped. Deterministic,
//                                              free — NO LLM call, NO api-key gate (mirrors introspect).
//                                              Chunked: the first call creates a session; subsequent
//                                              ≤4MB slices are posted with { append: true, sessionId }.
//                                              Use createAnalysisSession()/appendAnalysisSession() below
//                                              rather than calling this directly — they prefilter bodies
//                                              and handle the chunking.
//   POST /proxy/checks/estimate { rows?, app? } -> { profileTokens, rowCount, note }
//                                              Deterministic, free — NO LLM call, NO api-key
//                                              gate. Estimates the token size of the
//                                              profileSession(rows) profile that discover/
//                                              propose/audit would feed an LLM, so the UI can
//                                              show the token tradeoff of the current request
//                                              scope while the user is picking.
//   POST /proxy/checks/ask       { text, rows?, app?, sessionId? }
//                                              -> 200 { ok, answer, rowCount, error? }
//                                                 400 { error: 'text_required' }
//                                                 409 { error: 'anthropic_api_key_missing', detail }
//                                              Free-form Q&A over the requests in scope
//                                              (folds the old QA Observer into the Checks
//                                              surface). Same api-key gate as compile/
//                                              discover — user-initiated, so callers surface
//                                              errors rather than hiding them. `sessionId`
//                                              (from createAnalysisSession/appendAnalysisSession
//                                              below) sources rows from a server-side analysis
//                                              session INSTEAD of `rows`/live history when both
//                                              are present — see WP0, docs/interactive-analyst-spec.md.
//   POST /proxy/checks/introspect { rows?, app?, adOnly? } -> { requestTypes:[{type,count}],
//                                              custParamKeys:[{key,sample}],
//                                              urlParamKeys:[{key,sample}],
//                                              sources:[...],
//                                              adTraffic:{ total, byCategory:[{category,count}],
//                                                adRows, nonAdRows, unknownRows,
//                                                topUnknownHosts:[{host,count,adCategory}] } | null,
//                                              adOnly?, rowCount }
//                                              Deterministic, free — NO LLM call, NO api-key
//                                              gate (same profileSession(rows) as /estimate,
//                                              reshaped into a field inventory). Powers the
//                                              Author panel's "What can I check?" guidance so
//                                              the user sees what request types + params
//                                              actually exist in scope instead of guessing.
//                                              `adTraffic` is the coarse is-this-ad-related
//                                              breakdown (proxy-server/adTraffic/classifyAdTraffic.js).
//   GET  /proxy/spend[?days=]    -> { date, input, output, total, calls, capped,
//                                              cap, remaining, byModel, history? }
//                                              docs/llm-spend-visibility-spec.md. Today's LLM
//                                              token spend against the shared daily budget
//                                              (LLM_DAILY_TOKEN_CAP). `capped:false` means no
//                                              cap is configured on this proxy — `cap`/
//                                              `remaining` are then null. `?days=N` adds
//                                              `history` (oldest-first, ending today; days with
//                                              no calls are ABSENT, not zero-filled). Deterministic,
//                                              free — NO LLM call, NO api-key gate.
//
// `adOnly` (optional boolean) — every row-scoped endpoint below accepts it. When
// true the proxy filters the resolved rows to ad-relevant categories (ad_request,
// bid_request, creative, ad_tracking, vast, unknown, consent, ivt_verification —
// drops analytics and non_ad) BEFORE the engine sees them, so a run/ask/propose/audit can ignore
// first-party app chatter. introspect echoes `adOnly:true` back when it applied.
//
// Response shapes are echoed back to the caller as-is (parsed JSON) — the
// page does its own defensive optional-chaining on the fields it reads.

import { buildThreadContext } from '../utils/askThread';

const PROXY_API_BASE = (process.env.REACT_APP_PROXY_API_URL || '/proxy').replace(/\/$/, '');

/**
 * Internal: POST/GET JSON against the proxy REST API. Always resolves the
 * parsed JSON body on 2xx. On a non-2xx response, rejects with an Error
 * whose `.status` and `.body` carry the server's status code and parsed
 * error body (e.g. { error: 'anthropic_api_key_missing', detail: '...' }),
 * so callers can branch on the specific error code rather than just a message.
 */
async function request(path, init = {}) {
  let res;
  try {
    // Routes live under /proxy/checks/* on the proxy REST API. PROXY_API_BASE is
    // the host (no /proxy), so prepend it here — matches api/appPacks.js.
    res = await fetch(`${PROXY_API_BASE}/proxy${path}`, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...(init.headers || {}),
      },
    });
  } catch (e) {
    const err = new Error(`Could not reach the proxy server at ${PROXY_API_BASE}`);
    err.status = 0;
    err.body = null;
    err.cause = e;
    throw err;
  }

  let json = null;
  try { json = await res.json(); } catch { /* empty/non-JSON body */ }

  if (!res.ok) {
    const err = new Error((json && (json.detail || json.error)) || `${init.method || 'GET'} ${path} failed (${res.status})`);
    err.status = res.status;
    err.body = json;
    throw err;
  }
  return json;
}

/**
 * GET /proxy/checks/suites -> { suites: [{ name, count, source, updatedAt? }] }
 * Returns the server shape as-is (minus the envelope): each entry carries
 * `source` ('builtin' | 'user') so callers can badge/group and gate destructive
 * actions (only `source === 'user'` suites are appendable/deletable) plus an
 * optional `updatedAt`. Returns [] on a malformed/empty response.
 */
export async function listSuites() {
  const data = await request('/checks/suites', { method: 'GET' });
  return Array.isArray(data?.suites) ? data.suites : [];
}

/**
 * GET /proxy/checks/aps-params -> [{ key, label, meaning, severity, expected, platforms }]
 * The per-field APS reference the param detail view renders against, derived
 * server-side from knowledge/amazon-aps.json. Served rather than forked into a
 * frontend file, so there is no dictionary here to drift out of sync with the
 * pack (docs/aps-param-conformance-spec.md §6.1). Returns [] on a malformed or
 * unreachable response — the table then falls back to bare field keys rather
 * than rendering nothing.
 */
export async function getApsParamDictionary() {
  try {
    const data = await request('/checks/aps-params', { method: 'GET' });
    return Array.isArray(data?.params) ? data.params : [];
  } catch {
    return [];
  }
}

/**
 * GET /proxy/checks/prebid-params -> { params: { [ortbPath]: {name, meaning, severity, expected} },
 *                                      aliases: { [envelopePath]: ortbPath } }
 * The per-field Prebid reference, derived server-side from
 * knowledge/prebid-mobile.json. A MAP, not the array the APS route returns:
 * these rows go through the GAM pass's shared builder, whose dictionary
 * argument is keyed by param.
 *
 * `aliases` collapses the parsed-sourced checks (`request.app.bundle`) onto the
 * body path everything else uses (`app.bundle`), so one field renders as one
 * row. Both degrade to {} — the table then shows bare paths instead of nothing.
 */
export async function getPrebidParamDictionary() {
  try {
    const data = await request('/checks/prebid-params', { method: 'GET' });
    return {
      params: (data && data.params && typeof data.params === 'object') ? data.params : {},
      aliases: (data && data.aliases && typeof data.aliases === 'object') ? data.aliases : {},
    };
  } catch {
    return { params: {}, aliases: {} };
  }
}

/**
 * POST /proxy/checks/suites { name, checks[] } -> { ok, name, count }
 * Save (create or overwrite) a USER suite. `name` must match [a-z0-9-]{1,64};
 * builtin names are rejected. On a 400 the promise rejects with
 * err.body = { error: 'validation_failed', issues: [...] } so callers can render
 * the per-check issues legibly. Server-side names are lowercase — callers should
 * lowercase-coerce before sending.
 */
export async function saveSuite({ name, checks } = {}) {
  return request('/checks/suites', {
    method: 'POST',
    body: JSON.stringify({ name, checks: Array.isArray(checks) ? checks : [] }),
  });
}

/**
 * POST /proxy/checks/suites/<name>/append { check } -> { ok, name, count, replaced }
 * Append a single compiled Check to a USER suite, creating the suite if it does
 * not exist yet. `replaced` is true when a check with the same `id` was already
 * present and got overwritten. Same 400 { error:'validation_failed', issues }
 * contract as saveSuite() on an invalid name/check.
 */
export async function appendCheckToSuite(name, check) {
  return request(`/checks/suites/${encodeURIComponent(name)}/append`, {
    method: 'POST',
    body: JSON.stringify({ check }),
  });
}

/**
 * DELETE /proxy/checks/suites/<name> -> { ok, name } | 403 (builtin) | 404 (unknown)
 * Delete a USER suite. Rejects with err.status 403 for a builtin suite name and
 * 404 when the suite is unknown — callers surface those distinctly.
 */
export async function deleteSuite(name) {
  return request(`/checks/suites/${encodeURIComponent(name)}`, { method: 'DELETE' });
}

/**
 * GET /proxy/checks/suites/<name> -> { name, checks: [...Check] }
 * The FULL definition of every check in a named suite (builtin or user) —
 * same shape/idiom as getLaunchChecks() (api/launches.js: GET
 * /proxy/launches/<id>/checks), but keyed directly by suite name so a page
 * that isn't rendering inside a launch workspace (e.g. ChecksPage's baseline
 * auto-run, WP12 docs/checks-page-ux-spec.md §13) can still resolve
 * checkId -> Check (assert/target/description) for its own results. Never
 * throws its own shape issue — returns { name, checks: [] } on a malformed
 * response; a genuine network/HTTP failure still rejects (callers should
 * catch and degrade to the "no definition" fallback, same contract
 * loadCheckDefs() in LaunchPage.jsx already uses for getLaunchChecks()).
 */
export async function getSuiteChecks(name) {
  const data = await request(`/checks/suites/${encodeURIComponent(name)}`, { method: 'GET' });
  return { name: (data && typeof data.name === 'string') ? data.name : name, checks: Array.isArray(data?.checks) ? data.checks : [] };
}

/**
 * GET /proxy/checks/quarantine -> { quarantined: [{ checkId, since, flipCount,
 * window, app, suite, lastRunId, by:'auto'|'manual' }] }
 * Every check currently demoted by the flaky-check quarantine guardrail
 * (docs/coverage-guided-capture-spec.md §7-9): a fail verdict softened to warn
 * at evaluate time because it flip-flopped across comparable recent runs (or
 * was force-quarantined manually). Returns [] on a malformed/empty response so
 * the manager panel degrades to its empty state rather than crashing.
 */
export async function listQuarantine() {
  const data = await request('/checks/quarantine', { method: 'GET' });
  return Array.isArray(data?.quarantined) ? data.quarantined : [];
}

/**
 * POST /proxy/checks/quarantine/<checkId> { app?, suite? } -> { ok, entry }
 * Manually quarantine a check (`by: 'manual'` on the resulting entry) — rare,
 * but symmetric with the auto flip-scorer: a human can force a check into
 * quarantine (e.g. one they know is currently unreliable) without waiting for
 * three flips to accumulate. Softens fail->warn on every future evaluate-time
 * run until releaseCheck() is called.
 */
export async function quarantineCheck(checkId, { app, suite } = {}) {
  return request(`/checks/quarantine/${encodeURIComponent(checkId)}`, {
    method: 'POST',
    body: JSON.stringify({
      ...(app ? { app } : {}),
      ...(suite ? { suite } : {}),
    }),
  });
}

/**
 * POST /proxy/checks/quarantine/<checkId>/release -> { ok } | 404 when the
 * check isn't currently quarantined. Restores the check's real (pre-demotion)
 * verdict starting with its next run. Rejects with err.status === 404 when
 * nothing was quarantined under that id — callers should special-case it
 * (e.g. "already released") rather than treat it as a generic failure.
 */
export async function releaseCheck(checkId) {
  return request(`/checks/quarantine/${encodeURIComponent(checkId)}/release`, { method: 'POST' });
}

/**
 * GET /proxy/checks/runs?limit= -> { runs: [...] } (newest-first)
 * Recorded run history. Each run: { id, ts, app, suite, inline, adOnly,
 * appAdsDomain, rowCount, rollup:{pass,warn,fail,na,info},
 * checks:[{id,status,matched,found}], trigger:'run'|'audit'|'auto' }. Returns []
 * on a malformed/empty response so a history panel degrades to its empty state.
 */
export async function listRuns({ limit } = {}) {
  const qs = Number.isFinite(Number(limit)) ? `?limit=${Number(limit)}` : '';
  const data = await request(`/checks/runs${qs}`, { method: 'GET' });
  return Array.isArray(data?.runs) ? data.runs : [];
}

/**
 * GET /proxy/checks/runs/<id> -> a single run record (same shape as a runs[] entry).
 * Returns null on a malformed/empty response.
 */
export async function getRun(id) {
  const data = await request(`/checks/runs/${encodeURIComponent(id)}`, { method: 'GET' });
  return data && typeof data === 'object' ? data : null;
}

/**
 * GET /proxy/checks/trends?checkId=&limit= -> { checkId, trend:[{ runId, ts, status }] }
 * Per-check status history across recorded runs (newest-first). Returns
 * { checkId, trend: [] } on a malformed/empty response.
 */
export async function checkTrend(checkId, { limit } = {}) {
  const params = new URLSearchParams();
  if (checkId) params.set('checkId', checkId);
  if (Number.isFinite(Number(limit))) params.set('limit', String(Number(limit)));
  const data = await request(`/checks/trends?${params.toString()}`, { method: 'GET' });
  return { checkId, trend: Array.isArray(data?.trend) ? data.trend : [] };
}

/**
 * GET /proxy/knowledge -> { packs: [...] }
 * Vendor spec/SDK knowledge packs. The proxy (proxy-server/knowledge/*.json) is
 * the canonical source — frontend/src/knowledge/index.js fetches this at
 * runtime and caches the result instead of bundling its own duplicate copy.
 * Never throws — returns [] on any failure so callers can fire-and-forget.
 */
export async function getKnowledgePacks() {
  try {
    const data = await request('/knowledge', { method: 'GET' });
    return Array.isArray(data?.packs) ? data.packs : [];
  } catch {
    return [];
  }
}

/**
 * POST /proxy/checks/compile { text, app?, scopeTypes? } -> { ok, check, issues, rationale, raw }
 * `scopeTypes` is the distinct requestType(s) of the request(s) the user selected
 * on the Dashboard; when present the compiler hard-scopes target.requestType to
 * them instead of applying its broaden-to-family defaults.
 * On a 409 with body.error === 'anthropic_api_key_missing', the promise
 * rejects with that error attached at err.body — callers should special-case it.
 */
export async function compileCheck(text, app, scopeTypes) {
  const types = Array.isArray(scopeTypes) ? scopeTypes.filter(Boolean) : [];
  return request('/checks/compile', {
    method: 'POST',
    body: JSON.stringify({ text, ...(app ? { app } : {}), ...(types.length ? { scopeTypes: types } : {}) }),
  });
}

/**
 * POST /proxy/checks/run { suite?, checks?, app?, rows?, signals?, record? }
 * -> { suite, results, rollup, session, runId }
 * Pass either `suite` (a suite name loaded server-side) or an explicit
 * `checks` array (e.g. a single freshly-compiled Check) — not both are
 * required. `rows` is optional; omitted, the proxy uses its own live history.
 * `signals` is an optional discoverSignals() `proposal` — when present, the
 * proxy runs against signalsToAppPack(signals, appPack) instead of the app's
 * persisted App Pack config (nothing is persisted).
 *
 * IMPORTANT — `suite` and `checks` do NOT merge (verified against
 * apiServer.js's `/proxy/checks/run` handler, docs/launch-signoff-spec.md
 * §7): when `checks` is a non-empty array it is used VERBATIM as the run's
 * check set and `suite` is only kept as the response/run-history label —
 * the named suite's own stored checks are never loaded or executed
 * alongside it. A caller that needs both a stored suite's checks AND a set
 * of ad-hoc/other-suite checks evaluated together must issue two run calls
 * and merge the `results[]` client-side by `checkId` (see LaunchPage.jsx's
 * launch-workspace run, which does exactly this for its
 * `launch.suite` + existing-mapped-checkIds case).
 *
 * `runId` (WP2) is the id of the run-history record this call created —
 * `null` when `record:false` was passed (no history entry, no id to echo).
 * Defaults to recording (`record` omitted/true); pass `record:false` for a
 * scratch/merge-only run that shouldn't show up in run history (e.g. a
 * secondary lookup call whose results get folded into a primary recorded run).
 *
 * `mode` ('batch' | 'live', WP6 docs/mcp-signoff-spec.md §5) tags a RECORDED
 * run so the proxy's launch rollup (GET /proxy/launches/<id>/rollup) can tell
 * apart a batch run (LaunchPage's "Run checks" over selected/captured
 * traffic) from a live capture's finalize run (CapturePanel's recorded
 * `doFinalize` call) — both call this same function, so `trigger` alone
 * can't distinguish them server-side. Omit for calls that don't correspond
 * to either launch-workspace source (e.g. ChecksPage runs); a run recorded
 * without `mode` counts as 'batch' on the proxy (back-compat default).
 */
export async function runChecks({ suite, checks, app, rows, signals, selectionExplicit, adOnly, record, mode, gradedIds } = {}) {
  return request('/checks/run', {
    method: 'POST',
    body: JSON.stringify({
      ...(suite ? { suite } : {}),
      ...(Array.isArray(checks) ? { checks } : {}),
      ...(app ? { app } : {}),
      ...(Array.isArray(rows) ? { rows } : {}),
      ...(signals ? { signals } : {}),
      // Tell the engine the rows are a human's explicit pick, so a check's
      // requestType scope is honored-over by the selection (see /checks/run).
      ...(selectionExplicit ? { selectionExplicit: true } : {}),
      ...(adOnly ? { adOnly: true } : {}),
      ...(record === false ? { record: false } : {}),
      ...(mode === 'batch' || mode === 'live' ? { mode } : {}),
      // Per-check uncapped requestId lists for the evidence HAR
      // (docs/ticket-validation-workflow-spec.md §3). Opt-in: they're large.
      ...(gradedIds ? { gradedIds: true } : {}),
    }),
  });
}

/**
 * POST /proxy/checks/run-driven { suite?, checks?, app, platform?, windowSec?, device?, signals? }
 * -> { suite, app, platform, plan, results, rollup, session, driven }
 * `app` is required by the route (400 app_required if missing). `signals` —
 * see runChecks() above; same in-memory override, no persistence.
 */
export async function runChecksDriven({ suite, checks, app, platform = 'ios', windowSec, device, signals } = {}) {
  return request('/checks/run-driven', {
    method: 'POST',
    body: JSON.stringify({
      ...(suite ? { suite } : {}),
      ...(Array.isArray(checks) ? { checks } : {}),
      app,
      platform,
      ...(windowSec ? { windowSec } : {}),
      ...(device ? { device } : {}),
      ...(signals ? { signals } : {}),
    }),
  });
}

/**
 * POST /proxy/checks/discover { rows?, app? }
 * -> { ok, proposal:{ authSignal, contentApis:{mediaTagField,contentHosts}, labels },
 *      rationale, confidence, issues, raw, profile }
 * `rows` optional; omitted, the proxy uses its own live history. On a 409 with
 * body.error === 'anthropic_api_key_missing', the promise rejects with that
 * error attached at err.body — same shape/handling as compileCheck().
 */
export async function discoverSignals({ rows, app } = {}) {
  return request('/checks/discover', {
    method: 'POST',
    body: JSON.stringify({
      ...(Array.isArray(rows) ? { rows } : {}),
      ...(app ? { app } : {}),
    }),
  });
}

/**
 * POST /proxy/checks/propose-suite { rows?, app?, max? }
 * -> { ok, checks:[...Check], dropped:[{check,issues}], rationale, coverage:[...], issues, raw }
 * `rows` optional; omitted, the proxy uses its own live history. `max` caps how
 * many checks are proposed (proxy default 8). On a 409 with
 * body.error === 'anthropic_api_key_missing', the promise rejects with that
 * error attached at err.body — same shape/handling as discoverSignals().
 */
export async function proposeSuite({ rows, app, max, adOnly } = {}) {
  return request('/checks/propose-suite', {
    method: 'POST',
    body: JSON.stringify({
      ...(Array.isArray(rows) ? { rows } : {}),
      ...(app ? { app } : {}),
      ...(max ? { max } : {}),
      ...(adOnly ? { adOnly: true } : {}),
    }),
  });
}

/**
 * POST /proxy/checks/audit { rows?, app?, max? }
 * -> { ok, signals, proposal, run, usedDiscoveredSignals, checkSource, note? }
 * The capstone: chains discoverSignals -> proposeSuite -> runSuite into ONE
 * pass over a session — "audit this app," zero authoring. `rows` optional;
 * omitted, the proxy uses its own live history. `run` is the same shape
 * runChecks() returns (results/rollup/session). On a 409 with
 * body.error === 'anthropic_api_key_missing', the promise rejects with that
 * error attached at err.body — same shape/handling as discoverSignals()/proposeSuite().
 */
export async function auditSession({ rows, app, max, adOnly } = {}) {
  return request('/checks/audit', {
    method: 'POST',
    body: JSON.stringify({
      ...(Array.isArray(rows) ? { rows } : {}),
      ...(app ? { app } : {}),
      ...(max ? { max } : {}),
      ...(adOnly ? { adOnly: true } : {}),
    }),
  });
}

/**
 * POST /proxy/checks/estimate { rows?, app? } -> { profileTokens, rowCount, note }
 * Deterministic, free — no LLM call, no api-key gate. `rows` optional; omitted,
 * the proxy uses its own live history. Intended for a debounced UI readout as
 * the user changes request scope, so no special error handling here beyond
 * the shared `request()` behavior — callers should catch and hide the
 * readout on failure rather than surfacing an error.
 */
export async function estimateScope({ rows, app, adOnly } = {}) {
  return request('/checks/estimate', {
    method: 'POST',
    body: JSON.stringify({
      ...(Array.isArray(rows) ? { rows } : {}),
      ...(app ? { app } : {}),
      ...(adOnly ? { adOnly: true } : {}),
    }),
  });
}

/**
 * POST /proxy/checks/agent/prepare { rows?, app?, suite?, compile? }
 * -> 200 { ok, suite, total, applicable, skipped, counts, results, gaps,
 *          willSpend, compiled, notes }
 *    409 { error: 'anthropic_api_key_missing' }  — ONLY when compile:true
 *
 * The Agent tab's "Run baseline checks". Loads the committed baseline suite,
 * runs the checks that can match this session, and reports the coverage gap.
 *
 * SPENDS NOTHING by default. `willSpend` is how many gap rules an LLM WOULD
 * author — show it and get consent before calling again with compile:true.
 * A plain prepare works with no API key configured at all, so never gate the
 * button on a key.
 */
export async function agentPrepare({ rows, app, suite, compile, adOnly } = {}) {
  return request('/checks/agent/prepare', {
    method: 'POST',
    body: JSON.stringify({
      ...(Array.isArray(rows) ? { rows } : {}),
      ...(app ? { app } : {}),
      ...(suite ? { suite } : {}),
      ...(compile ? { compile: true } : {}),
      ...(adOnly ? { adOnly: true } : {}),
    }),
  });
}

/**
 * POST /proxy/checks/agent/ask { text, rows?, app?, prepared?, history?, depth? }
 * -> 200 { ok, answer, via, spent, check?, result?, ... }
 *    400 { error: 'text_required' }
 *    409 { error: 'anthropic_api_key_missing' }  — only when rung 0 misses
 *
 * The Agent tab's "Ask". `via` names the rung that answered — 'prepared' (free,
 * computed from the prepare() result you pass in), 'auto' (compiled then run,
 * model narrates) or 'analyst' (full investigation). Pass the prepare() result
 * as `prepared` or rung 0 can never fire and every question costs tokens.
 *
 * `model` picks the LLM for the rungs that use one (GET /proxy/models lists the
 * choices). Omitted or 'auto' leaves the escalation ladder alone; anything else
 * overrides it in both directions — a picked Haiku is never escalated, a picked
 * Opus is never demoted.
 *
 * `async: true` (docs/checks-ask-async-spec.md) asks the server to deliver a
 * PAID answer as a job instead of holding the HTTP request open for it, so an
 * investigation survives the socket dying mid-run. It changes the shape of what
 * resolves, and a caller passing it MUST branch on that:
 *
 *   { jobId, status:'running' }  — a rung that reaches a model. The answer is
 *                                  not here; it arrives as a `checks_ask_job`
 *                                  WS message, or via getAskJob(jobId).
 *   the usual answer object      — a FREE rung answered inline, instantly. The
 *                                  server never turns those into jobs.
 *
 * Errors are unchanged: every guard still rejects synchronously with its real
 * status, so a 409 anthropic_api_key_missing still throws here rather than
 * becoming a job that fails later.
 */
export async function agentAsk({ text, rows, app, prepared, history, depth, scopeTypes, adOnly, model, async: asyncMode, thread, searchChatHosts } = {}) {
  // `thread` is the prior turns of the conversation: either the already-built
  // context array (askThread.buildThreadContext output) or a thread object, which
  // is reduced to that context here. The server masks and re-bounds it (AC 117).
  const threadCtx = Array.isArray(thread) ? thread : buildThreadContext(thread);
  return request('/checks/agent/ask', {
    method: 'POST',
    body: JSON.stringify({
      text,
      ...(threadCtx.length ? { thread: threadCtx } : {}),
      // Human-set only (plan ruling 7): the dashboard sets no control for it in v1,
      // and the server accepts nothing but the boolean true.
      ...(searchChatHosts === true ? { searchChatHosts: true } : {}),
      ...(asyncMode === true ? { async: true } : {}),
      // Omitted (or 'auto') keeps the server's own Haiku-then-escalate ladder.
      ...(model && model !== 'auto' ? { model } : {}),
      ...(Array.isArray(rows) ? { rows } : {}),
      ...(app ? { app } : {}),
      ...(prepared ? { prepared } : {}),
      ...(Array.isArray(history) && history.length ? { history } : {}),
      ...(depth ? { depth } : {}),
      ...(Array.isArray(scopeTypes) && scopeTypes.length ? { scopeTypes } : {}),
      ...(adOnly ? { adOnly: true } : {}),
    }),
  });
}

/**
 * POST /proxy/checks/agent/compare { rows?, by?, where?, key?, limit?, detail? }
 * -> 200 { scopedFrom, rowCount, where?, by, headline, ... }
 *
 * "Which parameters differ between these two groups of requests?" FREE and
 * deterministic — pure string parsing, no model, no API key.
 *
 * `by` picks the axis: 'sdk' (default — app/GMA vs web/webview via the msid/an
 * markers), 'requestType', 'adUnit', or { param: '<name>' }.
 * `where` narrows first (loose, punctuation-blind match over every param value
 * and the URL) so "big bets" scopes to one article.
 * `key` focuses on ONE param and returns its ACTUAL VALUES per segment —
 * uncapped, and it reports EMPTY values separately from absent ones.
 *
 * Without `key`: { headline, findings[], sharedCount, totalKeys, truncated }.
 * With `key`:    { headline, segments[], verdict }.
 * Always carries scopedFrom/rowCount so a `where` that matched nothing is
 * obvious rather than looking like a clean pass.
 */
export async function agentCompare({ rows, by, where, key, limit, detail, adOnly } = {}) {
  return request('/checks/agent/compare', {
    method: 'POST',
    body: JSON.stringify({
      ...(Array.isArray(rows) ? { rows } : {}),
      ...(by ? { by } : {}),
      ...(where ? { where } : {}),
      ...(key ? { key } : {}),
      ...(Number.isFinite(limit) ? { limit } : {}),
      ...(detail ? { detail: true } : {}),
      ...(adOnly ? { adOnly: true } : {}),
    }),
  });
}

/**
 * POST /proxy/checks/ask { text, rows?, app? }
 * -> 200 { ok, answer, rowCount, error? }
 *    400 { error: 'text_required' }
 *    409 { error: 'anthropic_api_key_missing', detail }
 * Free-form natural-language question about the requests in scope — the old QA
 * Observer's Q&A, folded into the Checks surface (aggregation/forensics the
 * structured Checks can't express). `rows` optional; omitted, the proxy uses
 * its own live history. On a 409 with body.error === 'anthropic_api_key_missing',
 * the promise rejects with that error attached at err.body — same shape/handling
 * as compileCheck(). Unlike estimateScope(), this is user-initiated: callers
 * WILL surface errors rather than hiding them.
 */
export async function askTraffic({ text, rows, app, adOnly, history, agentic, sessionId, sessionRequired, depth } = {}) {
  return request('/checks/ask', {
    method: 'POST',
    body: JSON.stringify({
      text,
      ...(Array.isArray(rows) ? { rows } : {}),
      ...(app ? { app } : {}),
      ...(adOnly ? { adOnly: true } : {}),
      // `history` threads a follow-up onto a prior Q&A turn (≤6 {role,content}
      // pairs). `agentic:false` opts back to the single-shot digest answer; the
      // default (omitted) runs the analyst tool-loop.
      ...(Array.isArray(history) && history.length ? { history } : {}),
      ...(agentic === false ? { agentic: false } : {}),
      // `sessionId` (WP0 — createAnalysisSession/appendAnalysisSession below)
      // sources rows from a server-side analysis session instead of `rows`/live
      // history; wins over `rows` when both are present (proxy-side contract).
      ...(sessionId ? { sessionId } : {}),
      // `sessionRequired` (docs/capture-scoped-ask-spec.md WP2/WP4) marks
      // `sessionId` as naming a specific historical CAPTURE rather than the
      // analyst's live-traffic evidence stash — an unresolved session then
      // 409s instead of silently degrading to live traffic. Omit for the
      // evidence-stash path (default degrade-to-live behavior).
      ...(sessionRequired ? { sessionRequired: true } : {}),
      // `depth: 'deep'` (WP4/§5(a)) forces the analyst loop to escalate straight
      // to the Sonnet-tier model instead of the default Haiku pass. Frontend
      // "Deep analysis" toggle (WP6, ChecksPage.jsx) sets this.
      ...(depth === 'deep' ? { depth: 'deep' } : {}),
    }),
  });
}

/**
 * GET /proxy/agent/chat/jobs/<id> -> { ok, job } | 404 { error: 'job_not_found' }
 *
 * The recovery path for an async ask (docs/checks-ask-async-spec.md). The WS
 * push is the fast route; this is the one that still works when the socket that
 * would have carried the push was never open, died mid-run, or belongs to a tab
 * that has since been reloaded. A client that only listened for the push would
 * miss exactly the case the feature exists for.
 *
 * Resolves `null` rather than throwing when the job is gone — the store keeps
 * jobs for an hour and caps at 50, so "expired" is an ordinary outcome a caller
 * has to render, not an error worth a red box.
 */
export async function getAskJob(jobId) {
  if (!jobId) return null;
  try {
    const data = await request(`/agent/chat/jobs/${encodeURIComponent(jobId)}`, { method: 'GET' });
    return (data && data.job) || null;
  } catch (e) {
    if (e && e.status === 404) return null;
    throw e;
  }
}

/* ── Interactive Analyst WP0 — analysis-session client (chunked upload) ─────
 * docs/interactive-analyst-spec.md §3.2. A 154MB HAR's worth of rows can't go
 * up in one POST, and most of it (media segments, images) isn't ad-relevant
 * evidence anyway. `prefilterRowsForSession` drops body weight from rows
 * before upload (binary/large bodies stripped, row kept) and
 * `createAnalysisSession`/`appendAnalysisSession` slice the result into
 * ≤4MB POSTs against /proxy/checks/session. The Checks page UI is NOT wired
 * to call these yet (that's WP6) — they exist so WP6 and tests have a stable
 * client surface.
 */

// Bodies over this size are evidence-poor for ad-tech QA (media segments,
// images, fonts) and dominate upload weight — strip them, keep the row.
export const SESSION_PREFILTER_MAX_BODY_BYTES = 256 * 1024;
// Default per-POST slice size to /proxy/checks/session (spec: ≤4MB slices).
const DEFAULT_SESSION_CHUNK_BYTES = 4 * 1024 * 1024;

const BINARY_CONTENT_TYPE_RE =
  /^(image|video|audio|font)\//i;
const BINARY_CONTENT_TYPE_EXTRA_RE =
  /application\/(octet-stream|pdf|zip|gzip|x-protobuf|wasm|vnd\.apple\.mpegurl)|mpegurl|dash\+xml|mp2t/i;
const BINARY_EXT_RE =
  /\.(png|jpe?g|gif|webp|bmp|ico|svg|woff2?|ttf|eot|otf|mp4|m4s|m4a|m4v|mov|avi|webm|ts|m3u8|mpd|aac|mp3|wav|pdf|zip|gz|wasm)(?:\?|$)/i;

// Approximate UTF-8 byte length of a string. Uses TextEncoder when available
// (browser/jsdom/Node all have it); falls back to .length (close enough for a
// size THRESHOLD, not a precise cap) so this never throws in an odd runtime.
function byteLengthOf(str) {
  if (typeof str !== 'string' || !str) return 0;
  try {
    if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(str).length;
  } catch { /* fall through */ }
  return str.length;
}

// Cheap binary sniff over a text-typed field: a real binary blob smuggled
// into a JS string carries the U+FFFD replacement char (failed UTF-8 decode)
// or a dense run of control bytes. Never throws.
function looksBinaryString(body) {
  if (typeof body !== 'string' || !body) return false;
  try {
    const sample = body.slice(0, 512);
    if (sample.indexOf('�') !== -1) return true;
    let controlCount = 0;
    for (let i = 0; i < sample.length; i += 1) {
      const code = sample.charCodeAt(i);
      if (code === 0) return true;
      if (code < 32 && code !== 9 && code !== 10 && code !== 13) controlCount += 1;
    }
    return controlCount / Math.max(1, sample.length) > 0.05;
  } catch {
    return false;
  }
}

// Case-insensitive header lookup for content-type on the given side. Never throws.
function contentTypeOf(row, side) {
  try {
    const headers = side === 'request' ? row?.requestHeaders : row?.responseHeaders;
    if (!headers || typeof headers !== 'object') return '';
    for (const key of Object.keys(headers)) {
      if (key.toLowerCase() === 'content-type') return String(headers[key] || '');
    }
  } catch { /* ignore */ }
  return '';
}

// True when a row's URL/content-type/body content indicates binary content
// (media segments, images, fonts) — the class of body this prefilter strips
// regardless of size. Never throws.
function isBinaryBody(row, side, bodyStr) {
  try {
    const urlPath = String(row?.url || '').split('?')[0];
    if (BINARY_EXT_RE.test(urlPath)) return true;
    const ct = contentTypeOf(row, side);
    if (ct && (BINARY_CONTENT_TYPE_RE.test(ct) || BINARY_CONTENT_TYPE_EXTRA_RE.test(ct))) return true;
    if (looksBinaryString(bodyStr)) return true;
  } catch { /* never throw */ }
  return false;
}

/**
 * prefilterRowsForSession(rows) -> rows'
 * Pure, non-mutating. For each row, strips requestBody/responseBody that is
 * binary OR exceeds SESSION_PREFILTER_MAX_BODY_BYTES, stamping
 * `bodyStripped: 'client'` on the row when anything was stripped — the ROW is
 * always kept (identity/status/headers/validData survive), only the body
 * weight is dropped. Rows with no oversized/binary body pass through as-is
 * (same reference) so this is cheap to call on an already-slim session.
 * Tolerates any row shape — never throws.
 */
export function prefilterRowsForSession(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return list.map((row) => {
    if (!row || typeof row !== 'object') return row;
    let stripped = false;
    let out = row;
    for (const side of ['request', 'response']) {
      const field = side === 'request' ? 'requestBody' : 'responseBody';
      const body = row[field];
      if (body == null || body === '') continue;
      const bodyStr = typeof body === 'string' ? body : (() => { try { return JSON.stringify(body); } catch { return ''; } })();
      const bytes = byteLengthOf(bodyStr);
      if (bytes > SESSION_PREFILTER_MAX_BODY_BYTES || isBinaryBody(row, side, bodyStr)) {
        if (out === row) out = { ...row };
        out[field] = null;
        stripped = true;
      }
    }
    if (stripped) out.bodyStripped = 'client';
    return out;
  });
}

/**
 * chunkRowsByBytes(rows, maxBytes) -> rows[][]
 * Greedy-pack rows into slices whose stringified size stays ≤ maxBytes (a
 * single row larger than maxBytes on its own still gets its own slice rather
 * than being dropped). Pure, never throws.
 */
export function chunkRowsByBytes(rows, maxBytes = DEFAULT_SESSION_CHUNK_BYTES) {
  const list = Array.isArray(rows) ? rows : [];
  const chunks = [];
  let current = [];
  let currentBytes = 2; // '[' + ']'
  for (const row of list) {
    let size = 0;
    try { size = byteLengthOf(JSON.stringify(row)); } catch { size = 0; }
    if (current.length && currentBytes + size + 1 > maxBytes) {
      chunks.push(current);
      current = [];
      currentBytes = 2;
    }
    current.push(row);
    currentBytes += size + 1; // +1 for the joining comma
  }
  if (current.length) chunks.push(current);
  return chunks;
}

/**
 * createAnalysisSession({ rows, meta, chunkBytes? }) -> { sessionId, rowCount, bodiesRetained? }
 * Prefilters rows (prefilterRowsForSession), slices them into ≤chunkBytes
 * (default 4MB) POSTs, creates the session with the first slice, then appends
 * the rest with { append: true, sessionId }. Returns the LAST server
 * response (sessionId + running rowCount). If the initial create fails
 * (no sessionId back), remaining slices are skipped rather than orphan-appended.
 */
export async function createAnalysisSession({ rows, meta, chunkBytes } = {}) {
  const filtered = prefilterRowsForSession(rows);
  const chunks = chunkRowsByBytes(filtered, chunkBytes || DEFAULT_SESSION_CHUNK_BYTES);
  if (!chunks.length) {
    return request('/checks/session', {
      method: 'POST',
      body: JSON.stringify({ rows: [], ...(meta ? { meta } : {}) }),
    });
  }
  let result = await request('/checks/session', {
    method: 'POST',
    body: JSON.stringify({ rows: chunks[0], ...(meta ? { meta } : {}) }),
  });
  for (let i = 1; i < chunks.length; i += 1) {
    if (!result?.sessionId) break;
    // eslint-disable-next-line no-await-in-loop -- chunks must land in order
    result = await request('/checks/session', {
      method: 'POST',
      body: JSON.stringify({ rows: chunks[i], append: true, sessionId: result.sessionId }),
    });
  }
  return result;
}

/**
 * appendAnalysisSession(sessionId, rows, { chunkBytes? } = {}) -> { sessionId, rowCount }
 * Prefilters + chunks `rows` the same way createAnalysisSession does, then
 * appends each slice to an EXISTING session (e.g. adding freshly-captured
 * rows to a session already reused across a follow-up thread). Returns the
 * last server response.
 */
export async function appendAnalysisSession(sessionId, rows, { chunkBytes } = {}) {
  const filtered = prefilterRowsForSession(rows);
  const chunks = chunkRowsByBytes(filtered, chunkBytes || DEFAULT_SESSION_CHUNK_BYTES);
  if (!chunks.length) return { sessionId, rowCount: undefined };
  let result;
  for (const chunk of chunks) {
    // eslint-disable-next-line no-await-in-loop -- chunks must land in order
    result = await request('/checks/session', {
      method: 'POST',
      body: JSON.stringify({ rows: chunk, append: true, sessionId }),
    });
  }
  return result;
}

/**
 * POST /proxy/checks/auto { text, rows?, app?, scopeTypes?, driven?, platform?,
 *   device?, url?, sessionId? } -> { ok, question, checks, report, answer, narrated, sweep? }
 *   | { ok:false, stage, ... }
 * The one-shot "Auto" flow: compile the English requirement into a deterministic
 * Check, run it over the scoped rows, and narrate the findings. Requires the
 * proxy's ANTHROPIC_API_KEY (rejects 409 'anthropic_api_key_missing' otherwise —
 * callers should special-case it, same as compileCheck).
 * `driven:true` on ios/android drives the app to generate fresh traffic before
 * evaluating (requires `app` + Maestro on the proxy). Web is NOT drivable — the
 * proxy's driver-status reports webDrivable:false; a `platform:'web'` run
 * evaluates PASSIVELY over whatever traffic manual browsing already captured, so
 * `driven` is a no-op for web. `sweep` (when a mobile drive ran) mirrors the
 * scan-sweep shape: { steps, driven:{...}, note? }.
 * `sessionId` (docs/capture-scoped-ask-spec.md WP4) sources rows from a
 * server-side analysis session instead of `rows`/live history — wins over
 * `rows` when both are present, same contract as askTraffic's `sessionId`.
 */
/**
 * GET /proxy/checks/driver-status -> { maestro, drivable, apps, reason,
 *   chromium, webDrivable, webReason }
 * Whether this proxy can actually drive a device (Maestro installed + a real
 * app launch id) or a webpage (Playwright's Chromium installed — no per-app
 * config needed for web, an ad-hoc URL is enough). Used to show Driven as
 * unavailable up front.
 */
export async function driverStatus() {
  return request('/checks/driver-status', { method: 'GET' });
}

export async function autoRun({ text, rows, app, scopeTypes, driven, platform, device, url, adOnly, history, depth, sessionId, sessionRequired } = {}) {
  const types = Array.isArray(scopeTypes) ? scopeTypes.filter(Boolean) : [];
  return request('/checks/auto', {
    method: 'POST',
    body: JSON.stringify({
      text,
      ...(Array.isArray(rows) ? { rows } : {}),
      ...(app ? { app } : {}),
      ...(types.length ? { scopeTypes: types } : {}),
      ...(driven ? { driven: true, ...(platform ? { platform } : {}), ...(device ? { device } : {}), ...(url ? { url } : {}) } : {}),
      ...(adOnly ? { adOnly: true } : {}),
      // `history` threads a follow-up onto a prior question-intent answer.
      ...(Array.isArray(history) && history.length ? { history } : {}),
      // `depth:'deep'` lets a question-intent auto run escalate Haiku→Sonnet
      // (the compile/narrate stages stay Haiku; only the analyst path escalates).
      ...(depth === 'deep' ? { depth: 'deep' } : {}),
      // `sessionId` (WP4 — captureScopedAsk.js) sources rows from a
      // server-side analysis session instead of `rows`/live history; wins
      // over `rows` when both are present (proxy-side contract).
      ...(sessionId ? { sessionId } : {}),
      // `sessionRequired` (docs/capture-scoped-ask-spec.md WP2/WP4) marks
      // `sessionId` as naming a specific historical CAPTURE rather than the
      // analyst's live-traffic evidence stash — an unresolved session then
      // 409s instead of silently degrading to live traffic. Omit for the
      // evidence-stash/live path (default degrade-to-live behavior).
      ...(sessionRequired ? { sessionRequired: true } : {}),
    }),
  });
}

/**
 * POST /proxy/checks/introspect { rows?, app?, adOnly? } -> { requestTypes:[{type,count}],
 *   custParamKeys:[{key,sample}], urlParamKeys:[{key,sample}], sources:[...],
 *   adTraffic:{ total, byCategory, adRows, nonAdRows, unknownRows, topUnknownHosts } | null,
 *   adOnly?, rowCount }
 * Deterministic, free — no LLM call, no api-key gate. `rows` optional; omitted,
 * the proxy uses its own live history. `adOnly:true` scopes the field inventory
 * (and echoes `adOnly:true` back) to ad-relevant rows only. Intended to drive the
 * Author panel's "What can I check?" guidance — callers should catch and fall
 * back to an empty inventory on failure rather than surfacing an error (mirrors
 * estimateScope()'s defensive contract).
 */
export async function introspectScope({ rows, app, adOnly } = {}) {
  return request('/checks/introspect', {
    method: 'POST',
    body: JSON.stringify({
      ...(Array.isArray(rows) ? { rows } : {}),
      ...(app ? { app } : {}),
      ...(adOnly ? { adOnly: true } : {}),
    }),
  });
}

/**
 * GET /proxy/spend[?days=] -> { date, input, output, total, calls, capped,
 *   cap, remaining, byModel, history? }
 * Today's (proxy-local day) LLM token spend against the shared daily budget
 * (LLM_DAILY_TOKEN_CAP — see describeDegradation.js's doc comment for what
 * happens on the analyst path once it's hit). `capped` is whether a cap is
 * configured on this proxy at all — when false, `cap`/`remaining` are null
 * (an uncapped proxy, not "0% used"); callers must not divide by them.
 * Pass `days` (e.g. 14) to also get `history`: one entry per day THAT HAS
 * DATA, oldest-first, ending today — days with no LLM calls are ABSENT, not
 * zero-filled, so callers must not assume a fixed-length array. Deterministic,
 * free — NO LLM call, NO api-key gate (mirrors estimateScope()/
 * introspectScope()). Intended for a passive budget readout (SpendIndicator.jsx
 * / spendStatus.js) — callers should catch and degrade to a quiet inactive
 * state on failure rather than surfacing an error, same contract as
 * estimateScope().
 */
export async function getSpend({ days } = {}) {
  const n = Number(days);
  const qs = Number.isFinite(n) && n > 0 ? `?days=${n}` : '';
  return request(`/spend${qs}`, { method: 'GET' });
}

/**
 * GET /proxy/models -> { models: [{ id, label, detail, tier, inputPer1M?,
 * outputPer1M? }], default: 'auto' }
 *
 * The models Ask accepts. Fetched rather than hard-coded so the picker cannot
 * offer something the server would reject (or miss one it added).
 */
export async function listModels() {
  return request('/models');
}

/**
 * POST /proxy/checks/coverage { rows?, minPresence?, leavesOnly? }
 * -> { types: [{ requestType, rowCount, defined[], undefined[] }], totals }
 *
 * Which keys the captured traffic carries that the knowledge packs define, and
 * which they do not. Free — no model call, no API key.
 */
export async function paramCoverage({ rows, minPresence, leavesOnly } = {}) {
  return request('/checks/coverage', {
    method: 'POST',
    body: JSON.stringify({
      ...(Array.isArray(rows) ? { rows } : {}),
      ...(typeof minPresence === 'number' ? { minPresence } : {}),
      ...(leavesOnly === false ? { leavesOnly: false } : {}),
    }),
  });
}
